import { forwardRef, useId, useState } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { HelpTip } from './primitives';
import { Icon } from './Icon';

/**
 * Controles de formulario con etiqueta, ayuda, error y `aria-*` conectados.
 *
 * El mensaje de error se referencia con `aria-describedby` para que un lector de
 * pantalla lo anuncie: un borde rojo no es información accesible.
 */
export interface FieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  help?: string;
  error?: string;
  full?: boolean;
  children: ReactNode;
  errorId?: string;
}

export function Field({
  label,
  htmlFor,
  required,
  hint,
  help,
  error,
  full,
  children,
  errorId,
}: FieldProps): JSX.Element {
  return (
    <div className={`field${full === true ? ' form-grid__full' : ''}`}>
      {/* El botón de ayuda queda FUERA del <label>: dentro, pulsarlo activaría
          también el control asociado. */}
      <span className="field__label">
        <label htmlFor={htmlFor}>
          {label}
          {required === true && (
            <span className="field__req" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
        {help !== undefined && <HelpTip text={help} />}
      </span>
      {children}
      {hint !== undefined && error === undefined && <span className="field__hint">{hint}</span>}
      {error !== undefined && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  error?: string;
  hint?: string;
  help?: string;
  full?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, help, full, required, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  return (
    <Field
      label={label}
      htmlFor={inputId}
      required={required}
      hint={hint}
      help={help}
      error={error}
      full={full}
      errorId={errorId}
    >
      <input
        {...rest}
        id={inputId}
        ref={ref}
        required={required}
        className="input"
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? errorId : undefined}
      />
    </Field>
  );
});

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label: string;
  error?: string;
  hint?: string;
  help?: string;
  full?: boolean;
  children: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, help, full, required, id, children, ...rest },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const errorId = `${selectId}-error`;
  return (
    <Field
      label={label}
      htmlFor={selectId}
      required={required}
      hint={hint}
      help={help}
      error={error}
      full={full}
      errorId={errorId}
    >
      <select
        {...rest}
        id={selectId}
        ref={ref}
        required={required}
        className="select"
        aria-invalid={error !== undefined}
        aria-describedby={error !== undefined ? errorId : undefined}
      >
        {children}
      </select>
    </Field>
  );
});

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label: string;
  error?: string;
  hint?: string;
  help?: string;
  full?: boolean;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField({ label, error, hint, help, full, required, id, ...rest }, ref) {
    const generatedId = useId();
    const areaId = id ?? generatedId;
    const errorId = `${areaId}-error`;
    return (
      <Field
        label={label}
        htmlFor={areaId}
        required={required}
        hint={hint}
        help={help}
        error={error}
        full={full}
        errorId={errorId}
      >
        <textarea
          {...rest}
          id={areaId}
          ref={ref}
          required={required}
          className="textarea"
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? errorId : undefined}
        />
      </Field>
    );
  },
);

/** Campo de contraseña con botón mostrar/ocultar. */
export interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  autoComplete?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ label, error, hint, help, full, required, id, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    return (
      <Field
        label={label}
        htmlFor={inputId}
        required={required}
        hint={hint}
        help={help}
        error={error}
        full={full}
        errorId={errorId}
      >
        <div className="input-group">
          <input
            {...rest}
            id={inputId}
            ref={ref}
            required={required}
            type={visible ? 'text' : 'password'}
            className="input"
            aria-invalid={error !== undefined}
            aria-describedby={error !== undefined ? errorId : undefined}
          />
          <button
            type="button"
            className="input-group__btn"
            onClick={() => setVisible((current) => !current)}
            aria-pressed={visible}
            aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            <Icon name={visible ? 'eye-off' : 'eye'} size={15} />
          </button>
        </div>
      </Field>
    );
  },
);

export function CheckboxField({
  label,
  ...rest
}: { label: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'>): JSX.Element {
  return (
    <label className="checkbox">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}
